<template>
  <div class="flex flex-wrap gap-2 text-sm">
    <template v-if="showSaleBadge && !hasSaleReplacingBadge">
      <ProductBadge
        :badge-label="reductionLabel"
        :class="
          blackFridayLabels
            ? 'bg-black w-[75px] h-[38px] text-[20px] uppercase flex justify-center items-center'
            : 'bg-red'
        "
        class="!mt-0 rounded-sm text-white"
      />
    </template>

    <DynamicBadge
      v-for="badge in activeBadges"
      :key="`dynamic-${badge.label}`"
      :badge="badge"
      class="!mt-0"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import type { PropType } from 'vue'
import type { AppliedReduction, Product } from '@scayle/storefront-nuxt'
import { useDynamicBadges } from '#layers/base/composables/useDynamicBadges'
import useGlobalConfig from '~/composables/useGlobalConfig'
import { toCurrency } from '#layers/base/utils/price'

const props = defineProps({
  appliedReductions: {
    type: Array as PropType<AppliedReduction[]>,
    required: false,
    default: () => [],
  },
  product: {
    type: Object as PropType<Product>,
    required: true,
  },
  showSaleBadge: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
  showDiscountAsAbsolutePrice: {
    type: Boolean as PropType<boolean>,
    default: false,
  },
})

const { blackFridayLabels, config } = useGlobalConfig()
const { activeBadges } = useDynamicBadges(
  () => props.product,
  () => config.value?.dynamicBadges || [],
)

const hasSaleReplacingBadge = computed(() =>
  activeBadges.value.some((badge) => badge.replaceSale),
)

const reductionPercentage = computed(() => {
  if (props.appliedReductions?.[0]?.amount?.relative) {
    return Math.round(props.appliedReductions[0].amount.relative * 100)
  }
  return 0
})

const reductionAbsolute = computed(() => {
  // Show the overall absolute reduction: original - discounted.
  return (
    props.appliedReductions?.reduce(
      (sum, reduction) => sum + reduction.amount.absoluteWithTax,
      0,
    ) ?? 0
  )
})

const reductionLabel = computed(() => {
  if (props.showDiscountAsAbsolutePrice) {
    // appliedReductions amount is the discount value (absolute difference).
    const formatted = toCurrency(reductionAbsolute.value)

    // If the value ends with zero cents (e.g. 130,00), show just the integer part.
    // This strips only the fractional part when it is exactly ",00" / ".00".
    // Input is a short, locale-formatted currency string from toCurrency(); not user controlled.
    const withoutZeroCents = formatted.replace(
      // eslint-disable-next-line sonarjs/slow-regex
      /(\d[\d.,]*?)[.,]00(\s*\D*)$/,
      '$1$2',
    )

    return `-${withoutZeroCents}`
  }

  return `-${reductionPercentage.value}%`
})
</script>
